import React, { useState } from 'react';
import { VERB_SUGGESTIONS } from '@maco144/shared';
import {
  formatIdentity,
  getIdentityBadge,
  validatePoeticIdentity,
  type PoeticIdentity,
} from '@maco144/shared';
import type { UserId } from '@maco144/shared';
import { derivePoeticUserIdBrowser } from './poetic-identity-browser';

const IDENTITIES = {
  admin: {
    emoji: '⚙️',
    tagline: 'Manage and lead',
    nouns: [
      { name: 'Manager', desc: 'Oversee the space' },
      { name: 'Leader', desc: 'Guide the community' },
      { name: 'Keeper', desc: 'Maintain order' },
    ],
  },
  exploring: {
    emoji: '🔍',
    tagline: 'Discover & learn',
    nouns: [
      { name: 'Scout', desc: 'Map the terrain' },
      { name: 'Adventurer', desc: 'Seek the unknown' },
      { name: 'Observer', desc: 'Watch & listen' },
    ],
  },
  demo: {
    emoji: '🎯',
    tagline: 'Test & experiment',
    nouns: [
      { name: 'Tester', desc: 'Try new things' },
      { name: 'Visitor', desc: 'Just passing through' },
      { name: 'Experimenter', desc: 'Play & explore' },
    ],
  },
  building: {
    emoji: '🔨',
    tagline: 'Create & build',
    nouns: [
      { name: 'Creator', desc: 'Make something new' },
      { name: 'Developer', desc: 'Build systems' },
      { name: 'Maker', desc: 'Craft & design' },
    ],
  },
};

const ADJECTIVES_LIST = Object.entries(IDENTITIES).map(([name, data]) => ({
  name,
  emoji: data.emoji,
  nouns: data.nouns.map((n) => n.name),
}));

export function PoeticLogin({
  onAuth,
  loading = false,
}: {
  onAuth: (userId: UserId, identity: PoeticIdentity) => void;
  loading?: boolean;
}): JSX.Element {
  const [adjective, setAdjective] = React.useState(ADJECTIVES_LIST[0].name);
  const [noun, setNoun] = React.useState(ADJECTIVES_LIST[0].nouns[0]);
  const [verb, setVerb] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      console.log('PoeticLogin Container Debug:', {
        height: rect.height,
        width: rect.width,
        top: rect.top,
        bottom: rect.bottom,
        scrollHeight: containerRef.current.scrollHeight,
        clientHeight: containerRef.current.clientHeight,
        overflow: window.innerHeight - rect.bottom,
        windowHeight: window.innerHeight,
      });
    }
  }, [adjective, noun, verb]);

  const identity: PoeticIdentity = { adjective, noun, verb };
  const userId = derivePoeticUserIdBrowser(identity);
  const displayName = formatIdentity(identity);
  const badge = getIdentityBadge(identity);
  const validation = validatePoeticIdentity(identity);

  const handleLogin = () => {
    if (!validation.valid) {
      alert(validation.errors.join('\n'));
      return;
    }
    onAuth(userId, identity);
  };

  const handleRandomize = () => {
    const randomAdj = ADJECTIVES_LIST[Math.floor(Math.random() * ADJECTIVES_LIST.length)];
    const randomNoun = randomAdj.nouns[Math.floor(Math.random() * randomAdj.nouns.length)];
    const randomVerb = VERB_SUGGESTIONS[Math.floor(Math.random() * VERB_SUGGESTIONS.length)];

    setAdjective(randomAdj.name);
    setNoun(randomNoun);
    setVerb(randomVerb);
  };

  const adjData = IDENTITIES[adjective as keyof typeof IDENTITIES];
  const selectedNounData = adjData.nouns.find((n) => n.name === noun);

  return (
    <div
      style={{
        width: '100vw',
        minHeight: '100vh',
        background: `
          radial-gradient(ellipse at 80% 20%, rgba(217, 119, 6, 0.15) 0%, transparent 40%),
          radial-gradient(ellipse at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 40%),
          linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)
        `,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: "'EB Garamond', 'Georgia', serif",
        position: 'relative',
        overflow: 'auto',
      }}
    >
      <style>{globalLoginStyles}</style>

      {/* Animated background circles */}
      <div
        style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          border: '1px solid rgba(217, 119, 6, 0.1)',
          top: '-200px',
          right: '-200px',
          animation: 'slowFloat 20s infinite ease-in-out',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          border: '1px solid rgba(102, 126, 234, 0.1)',
          bottom: '-100px',
          left: '-100px',
          animation: 'slowFloat 25s infinite ease-in-out reverse',
        }}
      />

      <div
        ref={containerRef}
        style={{
          width: '100%',
          maxWidth: '1000px',
          padding: '20px 24px',
          background: 'transparent',
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px', flexShrink: 0 }}>
          <div style={{ fontSize: '56px', marginBottom: '8px' }}>🌅</div>
          <h1
            style={{
              fontSize: '36px',
              fontWeight: '300',
              color: '#f5ede0',
              margin: '0',
              letterSpacing: '0.5px',
            }}
          >
            Rising Sun
          </h1>
          <p
            style={{ fontSize: '14px', color: '#a0956f', margin: '6px 0 0 0', fontStyle: 'italic' }}
          >
            Choose your path
          </p>
        </div>

        {/* Main horizontal form */}
        <div
          style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0 }}
        >
          {/* Role Row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                fontSize: '14px',
                color: '#a0956f',
                fontWeight: '600',
                minWidth: '60px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              Role:
            </div>
            <div style={{ display: 'flex', gap: '12px', flex: 1 }}>
              {ADJECTIVES_LIST.map((adj) => {
                const isSelected = adjective === adj.name;
                return (
                  <button
                    key={adj.name}
                    onClick={() => {
                      setAdjective(adj.name);
                      setNoun(adj.nouns[0]);
                    }}
                    style={{
                      padding: '14px 20px',
                      borderRadius: '12px',
                      border: isSelected ? '2px solid #d97706' : '1px solid rgba(217, 119, 6, 0.3)',
                      background: isSelected
                        ? 'rgba(217, 119, 6, 0.15)'
                        : 'rgba(255, 255, 255, 0.02)',
                      color: '#f5ede0',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: "'EB Garamond', serif",
                      transition: 'all 0.2s',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      minWidth: '100px',
                    }}
                    onMouseEnter={(e) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.background = isSelected
                        ? 'rgba(217, 119, 6, 0.2)'
                        : 'rgba(217, 119, 6, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.background = isSelected
                        ? 'rgba(217, 119, 6, 0.15)'
                        : 'rgba(255, 255, 255, 0.02)';
                    }}
                  >
                    {adj.emoji} {adj.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Path Row */}
          {adjective && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  color: '#a0956f',
                  fontWeight: '600',
                  minWidth: '60px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                Path:
              </div>
              <div style={{ display: 'flex', gap: '12px', flex: 1 }}>
                {adjData.nouns.map((nounData) => {
                  const isSelected = noun === nounData.name;
                  return (
                    <button
                      key={nounData.name}
                      onClick={() => setNoun(nounData.name)}
                      style={{
                        padding: '12px 18px',
                        borderRadius: '10px',
                        border: isSelected
                          ? '2px solid #d97706'
                          : '1px solid rgba(217, 119, 6, 0.2)',
                        background: isSelected
                          ? 'rgba(217, 119, 6, 0.12)'
                          : 'rgba(255, 255, 255, 0.01)',
                        color: '#f5ede0',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap',
                        minWidth: '80px',
                      }}
                      onMouseEnter={(e) => {
                        const el = e.currentTarget as HTMLElement;
                        el.style.background = isSelected
                          ? 'rgba(217, 119, 6, 0.15)'
                          : 'rgba(217, 119, 6, 0.08)';
                      }}
                      onMouseLeave={(e) => {
                        const el = e.currentTarget as HTMLElement;
                        el.style.background = isSelected
                          ? 'rgba(217, 119, 6, 0.12)'
                          : 'rgba(255, 255, 255, 0.01)';
                      }}
                    >
                      {nounData.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action Row */}
          {adjective && noun && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  color: '#a0956f',
                  fontWeight: '600',
                  minWidth: '60px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                Do:
              </div>
              <input
                type="text"
                value={verb}
                onChange={(e) => setVerb(e.target.value.slice(0, 10).toLowerCase())}
                placeholder="explore..."
                style={{
                  flex: 1,
                  minWidth: '150px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '1px solid rgba(217, 119, 6, 0.3)',
                  background: 'rgba(217, 119, 6, 0.05)',
                  color: '#f5ede0',
                  fontSize: '14px',
                  fontFamily: "'EB Garamond', serif",
                  boxSizing: 'border-box',
                  transition: 'all 0.2s',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#d97706';
                  e.currentTarget.style.background = 'rgba(217, 119, 6, 0.08)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(217, 119, 6, 0.3)';
                  e.currentTarget.style.background = 'rgba(217, 119, 6, 0.05)';
                }}
              />
              <div
                style={{ fontSize: '12px', color: '#a0956f', minWidth: '28px', textAlign: 'right' }}
              >
                {verb.length}/10
              </div>
            </div>
          )}

          {/* Identity Preview - Compact */}
          {adjective && noun && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '8px',
                borderRadius: '8px',
                background: 'rgba(217, 119, 6, 0.06)',
                border: '1px solid rgba(217, 119, 6, 0.2)',
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: '16px' }}>{badge.emoji}</span>
              <span style={{ fontSize: '11px', fontWeight: '600', color: '#f5ede0' }}>
                {displayName}
              </span>
            </div>
          )}
        </div>

        {/* Action Buttons - Footer */}
        <div
          style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: '16px',
          }}
        >
          <button
            onClick={handleRandomize}
            disabled={loading}
            style={{
              padding: '14px 24px',
              borderRadius: '10px',
              border: '1px solid rgba(217, 119, 6, 0.3)',
              background: 'rgba(255, 255, 255, 0.03)',
              color: '#d97706',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              transition: 'all 0.2s',
              opacity: loading ? 0.5 : 1,
              fontFamily: "'EB Garamond', serif",
              whiteSpace: 'nowrap',
              minWidth: '140px',
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.background = 'rgba(217, 119, 6, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
              }
            }}
          >
            🎲 Randomize
          </button>
          <button
            onClick={handleLogin}
            disabled={!validation.valid || loading}
            style={{
              padding: '14px 32px',
              borderRadius: '10px',
              border: 'none',
              background:
                validation.valid && !loading
                  ? 'linear-gradient(135deg, #d97706 0%, #ea580c 100%)'
                  : 'rgba(217, 119, 6, 0.2)',
              color: validation.valid && !loading ? '#fff' : '#a0956f',
              cursor: validation.valid && !loading ? 'pointer' : 'not-allowed',
              fontWeight: '600',
              fontSize: '14px',
              opacity: validation.valid && !loading ? 1 : 0.5,
              transition: 'all 0.2s',
              fontFamily: "'EB Garamond', serif",
              whiteSpace: 'nowrap',
              minWidth: '140px',
            }}
            onMouseEnter={(e) => {
              if (validation.valid && !loading) {
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {loading ? 'Entering...' : '✨ Enter'}
          </button>
        </div>
      </div>
    </div>
  );
}

const globalLoginStyles = `
  * {
    box-sizing: border-box;
  }

  @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500;600;700&display=swap');

  body {
    margin: 0;
    padding: 0;
    overflow: hidden;
  }

  input:focus {
    outline: none;
  }

  input::placeholder {
    color: rgba(160, 149, 111, 0.6);
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-16px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes gentleFloat {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  @keyframes slowFloat {
    0%, 100% {
      transform: translate(0, 0);
    }
    25% {
      transform: translate(20px, -20px);
    }
    50% {
      transform: translate(0, -40px);
    }
    75% {
      transform: translate(-20px, -20px);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;
